// src/components/Blog/BlogView.tsx
import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import Header from '../Header/Header';
import Footer from '../Footer/Footer';
import BackToTop from '../Back-to-top/BackToTop';
import FeaturedProducts from '../Feature-Products/FeaturedProducts';
import {
  useBlogBySlug,
  useBlogComments,
  usePostComment,
  BlogPost,
} from '../../Hooks/useBlog';
import HeaderSpacer from '../Header/HeaderSpacer';

/* ── Toast ── */

interface ToastProps {
  type: 'success' | 'error';
  message: string;
  onClose: () => void;
}

const Toast: React.FC<ToastProps> = ({ type, message, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 5000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div
      style={{
        position: 'fixed', bottom: '30px', right: '30px', zIndex: 9999,
        display: 'flex', alignItems: 'center', gap: '12px',
        minWidth: '280px', maxWidth: '400px', padding: '14px 18px',
        borderRadius: '8px',
        backgroundColor: type === 'success' ? '#28a745' : '#dc3545',
        color: '#fff', boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
        animation: 'slideInUp 0.3s ease',
      }}
    >
      <span style={{ fontSize: '20px' }}>{type === 'success' ? '✓' : '✕'}</span>
      <span style={{ flex: 1, fontSize: '14px', lineHeight: '1.4' }}>{message}</span>
      <button
        onClick={onClose}
        style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: '18px', lineHeight: 1, padding: '0', opacity: 0.8 }}
        aria-label="Close notification"
      >
        &times;
      </button>
      <style>{`
        @keyframes slideInUp {
          from { transform: translateY(30px); opacity: 0; }
          to   { transform: translateY(0);   opacity: 1; }
        }
      `}</style>
    </div>
  );
};

/* ── Component ── */

const BlogView: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const locationBlog = location.state?.blog as BlogPost | undefined;

  const [comment, setComment] = useState({ name: '', email: '', content: '' });
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const closeToast = () => setToast(null);

  /* ── Queries ── */

  const { data: blog, isLoading: blogLoading } = useBlogBySlug(slug ?? '', locationBlog);

  const { data: comments = [], isLoading: commentsLoading } = useBlogComments(blog?.id);

  const postComment = usePostComment(blog?.id ?? '');

  /* ── Handlers ── */

  const handleCommentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!blog?.id) return;

    try {
      await postComment.mutateAsync({
        name: comment.name,
        email: comment.email,
        content: comment.content,
      });
      setComment({ name: '', email: '', content: '' });
      setToast({ type: 'success', message: 'Your comment has been posted successfully!' });
    } catch {
      setToast({ type: 'error', message: 'Failed to post comment. Please try again.' });
    }
  };

  /* ── Date helpers ── */

  const toDateSafe = (timestamp: any): Date | null => {
    if (!timestamp) return null;
    if (typeof timestamp.toDate === 'function') return timestamp.toDate();
    if (timestamp.seconds) return new Date(timestamp.seconds * 1000);
    if (typeof timestamp === 'string' || typeof timestamp === 'number') {
      const d = new Date(timestamp);
      return isNaN(d.getTime()) ? null : d;
    }
    return null;
  };

  const formatDate = (timestamp: any) => {
    const date = toDateSafe(timestamp);
    if (!date) return '';
    return date.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatDateShort = (timestamp: any) => {
    const date = toDateSafe(timestamp);
    if (!date) return { day: '', month: '' };
    return {
      day: String(date.getDate()).padStart(2, '0'),
      month: date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
    };
  };

  const formatCommentDate = (timestamp: any) => {
    const date = toDateSafe(timestamp);
    if (!date) return '';
    return (
      date.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' }) +
      ' at ' +
      date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    );
  };

  /* ── Loading / not found ── */

  if (blogLoading) {
    return (
      <>
        <Header />
      <HeaderSpacer />
        <div className="container p-t-80 p-b-80 txt-center"><p>Loading blog...</p></div>
        <Footer />
        <BackToTop />
      </>
    );
  }

  if (!blog) {
    return (
      <>
        <Header />
      <HeaderSpacer />
        <div className="container p-t-80 p-b-80 txt-center">
          <p>Blog not found</p>
          <button onClick={() => navigate('/blog')} className="stext-101 cl2 hov-cl1 trans-04 m-t-20">
            Back to Blog
          </button>
        </div>
        <Footer />
        <BackToTop />
      </>
    );
  }

  /* ── Data ── */

  const dateInfo = formatDateShort(blog.createdAt);
  const sortedParagraphs = [...blog.paragraphs].sort((a, b) => a.order - b.order);
  const submitting = postComment.isPending;

  /* ── Render ── */

  return (
    <>
      <Header />
      <HeaderSpacer />

      {toast && <Toast type={toast.type} message={toast.message} onClose={closeToast} />}

      <div className="container">
        <div className="bread-crumb flex-w p-t-30">
          <a onClick={() => navigate('/')} className="stext-109 cl8 hov-cl1 trans-04" style={{ cursor: 'pointer' }}>
            Home<i className="fa fa-angle-right m-l-9 m-r-10" />
          </a>
          <a onClick={() => navigate('/blog')} className="stext-109 cl8 hov-cl1 trans-04" style={{ cursor: 'pointer' }}>
            Blog<i className="fa fa-angle-right m-l-9 m-r-10" />
          </a>
          <span className="stext-109 cl4">{blog.title}</span>
        </div>
      </div>

      <section className="bg0 p-t-52 p-b-20">
        <div className="container">
          <div className="row">
            <div className="col-md-8 col-lg-9 p-b-80">
              <div className="p-r-45 p-r-0-lg">

                <div className="wrap-pic-w how-pos5-parent">
                  <img src={blog.backgroundImageUrl} alt={blog.title} />
                  <div className="flex-col-c-m size-123 bg9 how-pos5">
                    <span className="ltext-107 cl2 txt-center">{dateInfo.day}</span>
                    <span className="stext-109 cl3 txt-center">{dateInfo.month}</span>
                  </div>
                </div>

                <div className="p-t-32">
                  <span className="flex-w flex-m stext-111 cl2 p-b-19">
                    <span>
                      <span className="cl4">By</span> {blog.authorName}
                      <span className="cl12 m-l-4 m-r-6">|</span>
                    </span>
                    <span>{formatDate(blog.createdAt)}</span>
                  </span>

                  <h4 className="ltext-109 cl2 p-b-28">{blog.title}</h4>

                  {sortedParagraphs.map(p => (
                    <p key={p.id} className="stext-117 cl6 p-b-26">{p.content}</p>
                  ))}
                </div>

                <div className="flex-w flex-t p-t-16">
                  <span className="size-216 stext-116 cl8 p-t-4">Tags</span>
                  <div className="flex-w size-217">
                    {blog.tags.map((tag, i) => (
                      <span key={i} className="flex-c-m stext-107 cl6 size-301 bor7 p-lr-15 m-r-5 m-b-5">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

                {/* ── Comments List ── */}
                <div className="p-t-40">
                  <h5 className="mtext-113 cl2 p-b-12">
                    {comments.length > 0
                      ? `${comments.length} Comment${comments.length > 1 ? 's' : ''}`
                      : 'No Comments Yet'}
                  </h5>

                  {commentsLoading ? (
                    <p className="stext-117 cl6">Loading comments...</p>
                  ) : (
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                      {comments.map((c, index) => (
                        <li
                          key={c.id}
                          style={{
                            borderTop: index === 0 ? '1px solid #e6e6e6' : 'none',
                            borderBottom: '1px solid #e6e6e6',
                            padding: '20px 0',
                          }}
                        >
                          <div className="flex-w" style={{ gap: '16px', alignItems: 'flex-start' }}>
                            <div
                              style={{
                                width: '48px', height: '48px', borderRadius: '50%',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                flexShrink: 0, fontSize: '18px', fontWeight: 'bold',
                                color: '#fff', background: '#717fe0',
                              }}
                            >
                              {c.name.charAt(0).toUpperCase()}
                            </div>
                            <div style={{ flex: 1 }}>
                              <div className="flex-w flex-sb-m" style={{ marginBottom: '6px' }}>
                                <span className="stext-111 cl2" style={{ fontWeight: 600 }}>{c.name}</span>
                                <span className="stext-116 cl8" style={{ fontSize: '12px' }}>
                                  {formatCommentDate(c.createdAt)}
                                </span>
                              </div>
                              <p className="stext-117 cl6" style={{ margin: 0 }}>{c.content}</p>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* ── Comment Form ── */}
                <div className="p-t-40">
                  <h5 className="mtext-113 cl2 p-b-12">Leave a Comment</h5>

                  <form onSubmit={handleCommentSubmit}>
                    <textarea
                      className="stext-111 cl2 size-124 p-3 bor19 m-b-20"
                      placeholder="Comment *"
                      value={comment.content}
                      onChange={e => setComment({ ...comment, content: e.target.value })}
                      required
                      disabled={submitting}
                      style={{ width: '100%', resize: 'vertical' }}
                    />
                    <input
                      className="stext-111 cl2 size-116 p-3 bor19 m-b-20"
                      placeholder="Name *"
                      value={comment.name}
                      onChange={e => setComment({ ...comment, name: e.target.value })}
                      required
                      disabled={submitting}
                      style={{ display: 'block', width: '100%' }}
                    />
                    <input
                      className="stext-111 cl2 size-116 p-3 bor19 m-b-20"
                      placeholder="Email *"
                      type="email"
                      value={comment.email}
                      onChange={e => setComment({ ...comment, email: e.target.value })}
                      required
                      disabled={submitting}
                      style={{ display: 'block', width: '100%' }}
                    />
                    <button
                      className="flex-c-m stext-101 cl0 size-125 bg3 bor2 hov-btn3"
                      disabled={submitting}
                      style={{ opacity: submitting ? 0.6 : 1, cursor: submitting ? 'not-allowed' : 'pointer' }}
                    >
                      {submitting ? 'Posting...' : 'Post Comment'}
                    </button>
                  </form>
                </div>

              </div>
            </div>

            <div className="col-md-4 col-lg-3 p-b-80">
              <div className="side-menu">
                <FeaturedProducts />
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <BackToTop />
    </>
  );
};

export default BlogView;